import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api";

/**
 * TabsContext — Browser-style internal tabs for MyTACS.
 *
 * Behavior:
 *  - When the user navigates to a new route, a tab is auto-opened (unless one
 *    already exists for that route). The "current" tab follows React Router's
 *    location.
 *  - The sidebar stays accessible — clicking a sidebar link just navigates,
 *    which either focuses an existing tab or creates a new one.
 *  - Pinned tabs are pinned to the left and not affected by `resetTabs`.
 *  - Persisted per-user via PUT /api/auth/me/tabs (debounced) so they survive
 *    logout and reload.
 *  - No "+" button — tabs are created implicitly through navigation.
 */

const TabsCtx = createContext(null);

export const ROUTE_META = {
    "/":                   { title: "Dashboard",        icon: "dashboard" },
    "/calendar":           { title: "Kalender",         icon: "calendar" },
    "/banking":            { title: "Banking",          icon: "banking" },
    "/todos":              { title: "Aufgaben",         icon: "todos" },
    "/announcements":      { title: "Mitteilungen",     icon: "announcements" },
    "/employees":          { title: "Mitarbeiter",      icon: "employees" },
    "/vacations":          { title: "Urlaub",           icon: "vacations" },
    "/chat":               { title: "Chat",             icon: "chat" },
    "/settings":           { title: "Einstellungen",    icon: "settings" },
    "/superchat":          { title: "Superchat",        icon: "superchat" },
    "/whatsapp":           { title: "WhatsApp",         icon: "whatsapp" },
    "/claude":             { title: "Claude AI",        icon: "claude" },
    "/mail":               { title: "E-Mail",            icon: "mail" },
    "/notes":              { title: "Notizen",          icon: "notes" },
    "/zeiterfassung":      { title: "Zeiterfassung",    icon: "timetrack" },
    "/drive":              { title: "Drive",            icon: "drive" },
    "/admin":              { title: "Admin",            icon: "admin" },
};

function metaFor(pathname) {
    if (ROUTE_META[pathname]) return ROUTE_META[pathname];
    // Dynamic routes
    if (pathname.startsWith("/space/")) return { title: "Abteilung", icon: "space" };
    return { title: pathname.replace(/^\//, "") || "MyTACS", icon: "dashboard" };
}

const SKIP_TAB_ROUTES = new Set(["/login"]);

export function TabsProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [tabs, setTabs] = useState([]);
    const [hydrated, setHydrated] = useState(false);
    const saveTimer = useRef(null);
    const lastSavedRef = useRef("");

    // Hydrate from backend
    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await api.get("/auth/me/tabs");
                if (!cancel) {
                    setTabs((data?.tabs || []).map((t) => ({ ...t, pinned: !!t.pinned })));
                    lastSavedRef.current = JSON.stringify(data?.tabs || []);
                }
            } catch (_) {
                // First-time user — no tabs yet
            } finally {
                if (!cancel) setHydrated(true);
            }
        })();
        return () => { cancel = true; };
    }, []);

    // Persist (debounced) whenever tabs change
    useEffect(() => {
        if (!hydrated) return;
        const payload = JSON.stringify(tabs);
        if (payload === lastSavedRef.current) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await api.put("/auth/me/tabs", { tabs });
                lastSavedRef.current = payload;
            } catch (_) { /* ignore */ }
        }, 600);
        return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    }, [tabs, hydrated]);

    // Auto-open a tab whenever the user navigates to a new route.
    useEffect(() => {
        if (!hydrated) return;
        const route = location.pathname + location.search;
        if (SKIP_TAB_ROUTES.has(location.pathname)) return;
        setTabs((prev) => {
            if (prev.some((t) => t.route === route)) return prev;
            const meta = metaFor(location.pathname);
            const newTab = {
                id: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                route,
                title: meta.title,
                icon: meta.icon,
                pinned: false,
                opened_at: new Date().toISOString(),
            };
            return [...prev, newTab];
        });
    }, [location.pathname, location.search, hydrated]);

    const activeRoute = location.pathname + location.search;

    const focusTab = useCallback((route) => {
        navigate(route);
    }, [navigate]);

    const closeTab = useCallback((id) => {
        setTabs((prev) => {
            const idx = prev.findIndex((t) => t.id === id);
            if (idx < 0) return prev;
            const closingActive = prev[idx].route === activeRoute;
            const next = prev.filter((t) => t.id !== id);
            if (closingActive) {
                const fallback = next[idx] || next[idx - 1] || next[next.length - 1];
                if (fallback) {
                    setTimeout(() => navigate(fallback.route), 0);
                } else {
                    setTimeout(() => navigate("/"), 0);
                }
            }
            return next;
        });
    }, [activeRoute, navigate]);

    const togglePin = useCallback((id) => {
        setTabs((prev) => {
            const target = prev.find((t) => t.id === id);
            if (!target) return prev;
            const updated = prev.map((t) => t.id === id ? { ...t, pinned: !t.pinned } : t);
            // Reorder: pinned first, then unpinned, preserving relative order.
            const pinned = updated.filter((t) => t.pinned);
            const rest = updated.filter((t) => !t.pinned);
            return [...pinned, ...rest];
        });
    }, []);

    const renameTab = useCallback((id, title) => {
        setTabs((prev) => prev.map((t) => t.id === id ? { ...t, title } : t));
    }, []);

    const resetTabs = useCallback(() => {
        setTabs((prev) => {
            const keptPinned = prev.filter((t) => t.pinned);
            // Navigate to first remaining or dashboard
            const target = keptPinned[0]?.route || "/";
            setTimeout(() => navigate(target), 0);
            return keptPinned;
        });
    }, [navigate]);

    const closeAll = useCallback(() => {
        setTabs([]);
        setTimeout(() => navigate("/"), 0);
    }, [navigate]);

    return (
        <TabsCtx.Provider value={{
            tabs,
            activeRoute,
            focusTab,
            closeTab,
            togglePin,
            renameTab,
            resetTabs,
            closeAll,
        }}>
            {children}
        </TabsCtx.Provider>
    );
}

export function useTabs() {
    const ctx = useContext(TabsCtx);
    if (!ctx) throw new Error("useTabs must be used inside TabsProvider");
    return ctx;
}
